import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, Sparkles, X } from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type { InsightDrillTarget, ServerApiClient } from '../../../data/ServerApiClient'
import {
  DEFAULT_AI_REASONING_EFFORT,
  getModelIdForProvider,
  providerForAiRequest,
  readAiSettings,
} from '../../../ai/settings'
import { useCfoAgent } from '../../../ai/cfoAgent'
import { isOpenEndedAskEnabled } from '../../../ai/openEndedAsk'
import { AiFeedbackControl, ReportProblemButton, newAiTraceId, useAnalytics } from '../../../analytics'
import { JourneyChart } from './JourneyChart'
import type { JourneyData, JourneyDataSourceKind, JourneyDef, JourneyStepContent } from './types'
import './JourneyShell.css'

// JourneyShell renders an Option-C guided journey (plan Section 3) as a
// contained overlay over the Brief. It loads only the shared server contracts
// the journey's steps declare, renders each step's pure `build` output
// (narration + focused chart + mini-takeaway cards + depth-on-demand), threads
// the workbook-wide CFO agent for the per-step copilot, instruments the journey
// lifecycle (journey_started / journey_step_viewed / journey_completed), and
// hands the final step off to the existing presentation flow.

export type JourneyShellProps = {
  journey: JourneyDef
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  onClose: () => void
  /** Open/create the existing cfo-presentation tab (Wrap & share handoff). */
  onOpenPresentation?: () => void
  /**
   * Deep-link into a CFO Core command group (e.g. Ratios & Valuation, the
   * forecast workspace) for `cfo_core` hand-off steps. Mirrors the Brief's
   * insight-card drill-through — the host switches to the cfo-core tab and
   * hands the drill target to the CFO Core surface.
   */
  onOpenCfoCore?: (target?: InsightDrillTarget | null) => void
}

const SURFACE = 'cfo_journey'

const SEVERITY_LABEL: Record<string, string> = {
  good: 'Good',
  check: 'Check',
  watch: 'Watch',
  bad: 'Watch',
}

const trimStr = (value: unknown): string => String(value ?? '').trim()

const EMPTY_DATA: JourneyData = { comparisons: null, insights: null, cashPlanning: null }

export function JourneyShell({
  journey,
  appApi,
  workerClient = null,
  workbookId,
  onClose,
  onOpenPresentation,
  onOpenCfoCore,
}: JourneyShellProps) {
  const analytics = useAnalytics()
  const [stepIndex, setStepIndex] = useState(0)
  const [data, setData] = useState<JourneyData>(EMPTY_DATA)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [depthOpen, setDepthOpen] = useState(false)

  const aiSettings = useMemo(() => readAiSettings(), [])
  const aiRequestProvider = providerForAiRequest(aiSettings.provider)
  const aiModelId = getModelIdForProvider(aiSettings, aiSettings.provider)

  const cfoAgent = useCfoAgent({ appApi, workerClient, workbookId })
  const [agentBusy, setAgentBusy] = useState(false)
  const [agentError, setAgentError] = useState('')
  const [stepAnswer, setStepAnswer] = useState<{ stepId: string; text: string; traceId: string } | null>(null)

  // Stable trace ids per step so feedback on the deterministic narration
  // correlates to the step it was generated for.
  const traceIdsRef = useRef<Map<string, string>>(new Map())
  const traceIdForStep = useCallback((stepId: string): string => {
    const existing = traceIdsRef.current.get(stepId)
    if (existing) return existing
    const next = newAiTraceId()
    traceIdsRef.current.set(stepId, next)
    return next
  }, [])

  const requiredSources = useMemo<Set<JourneyDataSourceKind>>(() => {
    const set = new Set<JourneyDataSourceKind>()
    for (const step of journey.steps) for (const src of step.dataSources) set.add(src)
    return set
  }, [journey])

  // Load the union of declared data sources once per workbook/journey.
  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this view.')
      return () => {
        cancelled = true
      }
    }
    setLoading(true)
    setError('')
    analytics.track({
      event: 'journey_started',
      surface: SURFACE,
      workbook_id: wb,
      props: { journey: journey.id, step_count: journey.steps.length },
    })
    const tasks: Array<Promise<void>> = []
    const next: JourneyData = { ...EMPTY_DATA }
    if (requiredSources.has('comparisons')) {
      tasks.push(
        appApi
          .getQuickBooksMetricComparisons(wb, { grain: 'month' })
          .then((r) => {
            next.comparisons = r
          }),
      )
    }
    if (requiredSources.has('insights')) {
      tasks.push(
        appApi.getQuickBooksInsights(wb, { grain: 'month' }).then((r) => {
          next.insights = r
        }),
      )
    }
    if (requiredSources.has('cash_planning')) {
      tasks.push(
        appApi.getQuickBooksCashPlanning(wb).then((r) => {
          next.cashPlanning = r
        }),
      )
    }
    Promise.all(tasks)
      .then(() => {
        if (cancelled) return
        setData(next)
      })
      .catch((err: any) => {
        if (cancelled) return
        setError(trimStr(err?.message || err) || 'Could not load this journey.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // analytics is a stable singleton wrapper; reload only on journey/workbook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appApi, journey.id, requiredSources, workbookId])

  const stepCount = journey.steps.length
  const activeStep = journey.steps[Math.min(stepIndex, stepCount - 1)]
  const content: JourneyStepContent | null = useMemo(() => {
    if (loading || !activeStep) return null
    try {
      return activeStep.build(data)
    } catch {
      return { narration: 'This step could not be assembled from the available data.' }
    }
  }, [activeStep, data, loading])

  // Emit a view event once the active step's content is ready.
  useEffect(() => {
    if (loading || !activeStep) return
    analytics.track({
      event: 'journey_step_viewed',
      surface: SURFACE,
      workbook_id: workbookId || null,
      props: { journey: journey.id, step: activeStep.id, step_index: stepIndex },
    })
    setDepthOpen(false)
    // analytics is a stable singleton wrapper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, journey.id, loading, stepIndex, workbookId])

  const completedRef = useRef(false)
  const markCompleted = useCallback(
    (via: string) => {
      if (completedRef.current) return
      completedRef.current = true
      analytics.track({
        event: 'journey_completed',
        surface: SURFACE,
        workbook_id: workbookId || null,
        props: { journey: journey.id, via, step_count: stepCount },
      })
    },
    [analytics, journey.id, stepCount, workbookId],
  )

  const goToStep = useCallback(
    (index: number) => {
      setStepIndex(Math.max(0, Math.min(index, stepCount - 1)))
    },
    [stepCount],
  )

  const askCopilot = useCallback(async () => {
    if (!activeStep || agentBusy) return
    const prompt =
      trimStr(content?.copilotPrompt) ||
      `In the "${journey.title}" journey, summarize the "${activeStep.title}" step for a small-business owner and tell me what to do next.`
    setAgentBusy(true)
    setAgentError('')
    setStepAnswer(null)
    try {
      const resp = await cfoAgent.sendMessage(
        prompt,
        {
          provider: aiRequestProvider,
          model_id: aiRequestProvider ? aiModelId || null : null,
          workbook_id: workbookId || null,
          active_section: 'cfo_journey',
          reasoning_effort: DEFAULT_AI_REASONING_EFFORT,
        },
        {
          onAnswer: (r) => {
            analytics.track({
              event: 'ai_answer_rendered',
              surface: SURFACE,
              ai_trace_id: r.agent_run_id,
              workbook_id: workbookId || null,
              props: { journey: journey.id, step: activeStep.id },
            })
          },
        },
      )
      const text =
        [trimStr(resp.answer?.headline), trimStr(resp.answer?.executive_summary)].filter(Boolean).join(' ') ||
        'Done.'
      setStepAnswer({ stepId: activeStep.id, text, traceId: resp.agent_run_id })
    } catch (err: any) {
      setAgentError(trimStr(err?.message || err) || 'The copilot could not answer that.')
    } finally {
      setAgentBusy(false)
    }
  }, [
    activeStep,
    agentBusy,
    aiModelId,
    aiRequestProvider,
    analytics,
    cfoAgent,
    content?.copilotPrompt,
    journey.id,
    journey.title,
    workbookId,
  ])

  const handoff = content?.handoff ?? null

  const handleHandoff = useCallback(() => {
    if (!handoff) return
    if (handoff.kind === 'presentation') {
      markCompleted('presentation_handoff')
      onOpenPresentation?.()
      onClose()
      return
    }
    // cfo_core: deep-link into a CFO Core command group via the shared host.
    markCompleted('cfo_core_handoff')
    onOpenCfoCore?.(handoff.target)
    onClose()
  }, [handoff, markCompleted, onClose, onOpenCfoCore, onOpenPresentation])

  const isLastStep = stepIndex >= stepCount - 1

  const handleContinue = useCallback(() => {
    if (handoff) {
      handleHandoff()
      return
    }
    if (isLastStep) {
      markCompleted('finished')
      onClose()
      return
    }
    goToStep(stepIndex + 1)
  }, [goToStep, handleHandoff, handoff, isLastStep, markCompleted, onClose, stepIndex])

  const handoffLabel = handoff
    ? trimStr(handoff.label) ||
      (handoff.kind === 'presentation' ? 'Open the presentation' : 'Open in CFO Core')
    : ''

  const narrationTraceId = activeStep ? traceIdForStep(activeStep.id) : ''
  const showStepAnswer = stepAnswer && activeStep && stepAnswer.stepId === activeStep.id

  return (
    <div className="fg-journey" role="dialog" aria-modal="true" aria-label={`${journey.title} journey`}>
      <div className="fg-journey-topbar">
        <div className="fg-journey-crumb">
          <button type="button" className="fg-journey-link" onClick={onClose}>
            Home
          </button>
          <span> · </span>
          <b>{journey.title}</b>
        </div>
        <span className="fg-journey-sub">{journey.subtitle}</span>
        <button type="button" className="fg-journey-close" aria-label="Close journey" onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      <div className="fg-journey-body">
        <div className="fg-journey-stepper" role="tablist" aria-label="Journey steps">
          {journey.steps.map((step, i) => {
            const state = i < stepIndex ? 'done' : i === stepIndex ? 'active' : 'todo'
            return (
              <button
                type="button"
                key={step.id}
                role="tab"
                aria-selected={i === stepIndex}
                className={`fg-journey-step ${state}`}
                onClick={() => goToStep(i)}
              >
                <span className="fg-journey-step-n">{state === 'done' ? <Check size={12} /> : i + 1}</span>
                {step.shortTitle}
              </button>
            )
          })}
        </div>

        {error ? (
          <div className="fg-journey-error" role="alert">
            {error}
          </div>
        ) : loading || !content || !activeStep ? (
          <div className="fg-journey-loading">Assembling this step…</div>
        ) : (
          <>
            <div className="fg-journey-narr">
              <div className="fg-journey-narr-head">
                <span className="fg-journey-ai">
                  <Sparkles size={15} />
                </span>
                <h2>{activeStep.title}</h2>
              </div>
              <p>{content.narration}</p>
              <div className="fg-journey-narr-fb">
                <span className="fg-journey-tag">
                  <Sparkles size={11} /> AI narration · from your QuickBooks data
                </span>
                <AiFeedbackControl
                  aiTraceId={narrationTraceId}
                  surface={`${SURFACE}:narration`}
                  workbookId={workbookId}
                  props={{ journey: journey.id, step: activeStep.id }}
                />
                <ReportProblemButton
                  surface={`${SURFACE}:narration`}
                  aiTraceId={narrationTraceId}
                  context={{ journey: journey.id, step: activeStep.id }}
                />
              </div>
            </div>

            <div className="fg-journey-grid">
              <div className="fg-journey-panel">
                <div className="fg-journey-panel-head">
                  <h3>{content.chartTitle || 'Focused view'}</h3>
                  {content.chartMeta ? <span className="fg-journey-meta">{content.chartMeta}</span> : null}
                </div>
                <div className="fg-journey-panel-body">
                  {content.chart ? (
                    <JourneyChart spec={content.chart} />
                  ) : (
                    <div className="fg-journey-chart-empty">No chart for this step.</div>
                  )}
                </div>
              </div>

              <div className="fg-journey-panel">
                <div className="fg-journey-panel-head">
                  <h3>This step’s takeaways</h3>
                  {content.depth ? (
                    <button type="button" className="fg-journey-link" onClick={() => setDepthOpen((v) => !v)}>
                      {depthOpen ? 'Hide detail' : 'Go deeper →'}
                    </button>
                  ) : null}
                </div>
                <div className="fg-journey-panel-body fg-journey-takeaways">
                  {(content.takeaways || []).length > 0 ? (
                    (content.takeaways || []).map((t) => (
                      <div className={`fg-journey-takeaway ${t.severity}`} key={t.id}>
                        <span className={`fg-journey-pill ${t.severity}`}>{SEVERITY_LABEL[t.severity] || 'Note'}</span>
                        <div>
                          <h4>{t.headline}</h4>
                          {t.body ? <p>{t.body}</p> : null}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="fg-journey-muted">No flagged takeaways for this step.</div>
                  )}
                  {depthOpen && content.depth ? (
                    <div className="fg-journey-depth">
                      <b>{content.depth.label}</b>
                      <p>{content.depth.detail}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {showStepAnswer ? (
              <div className="fg-journey-answer">
                <div className="fg-journey-answer-head">
                  <Sparkles size={14} /> Copilot
                </div>
                <p>{stepAnswer!.text}</p>
                <div className="fg-journey-narr-fb">
                  <AiFeedbackControl
                    aiTraceId={stepAnswer!.traceId}
                    surface={`${SURFACE}:copilot`}
                    workbookId={workbookId}
                    props={{ journey: journey.id, step: activeStep.id }}
                  />
                  <ReportProblemButton surface={`${SURFACE}:copilot`} aiTraceId={stepAnswer!.traceId} />
                </div>
              </div>
            ) : agentError ? (
              <div className="fg-journey-error" role="alert">
                {agentError}
              </div>
            ) : null}

            <div className="fg-journey-foot">
              <button type="button" className="fg-journey-btn" onClick={onClose}>
                <ArrowLeft size={14} /> Back to Brief
              </button>
              <div className="fg-journey-spacer" />
              {isOpenEndedAskEnabled() ? (
                <button type="button" className="fg-journey-btn teal" onClick={askCopilot} disabled={agentBusy}>
                  <Sparkles size={14} /> {agentBusy ? 'Asking…' : 'Ask the copilot'}
                </button>
              ) : null}
              <button type="button" className="fg-journey-btn primary" onClick={handleContinue}>
                {handoff
                  ? handoffLabel
                  : isLastStep
                    ? 'Finish'
                    : `Continue to ${journey.steps[stepIndex + 1]?.shortTitle || 'next'}`}
                {!handoff && !isLastStep ? <ArrowRight size={14} /> : null}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default JourneyShell
